import { Grid, List } from "lucide-react";
import React, { useEffect } from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export type ViewType = "table" | "grid";

interface ViewToggleProps {
  viewType: ViewType;
  setViewType: (viewType: ViewType) => void;
}

// Create a specific event name constant to ensure consistency
export const VIEW_TYPE_CHANGE_EVENT = "app:view-type-changed";

export const ViewToggle: React.FC<ViewToggleProps> = ({
  viewType,
  setViewType,
}) => {
  // Dispatch custom event when viewType changes
  useEffect(() => {
    // Use a global event name to avoid conflicts
    const event = new CustomEvent(VIEW_TYPE_CHANGE_EVENT, {
      detail: { viewType },
      bubbles: true, // Allow event to bubble up the DOM tree
    });

    // Dispatch from document for better compatibility
    document.dispatchEvent(event);
  }, [viewType]);

  return (
    <Tabs
      value={viewType}
      onValueChange={(value) => setViewType(value as ViewType)}
      className='w-auto'
    >
      <TabsList className='bg-muted border border-border shadow-sm'>
        <TabsTrigger
          value='table'
          className={cn(
            "flex items-center gap-2",
            viewType === "table"
              ? "bg-primary text-primary-foreground"
              : "hover:bg-background/80",
          )}
        >
          <List className='h-4 w-4' />
          <span>List</span>
        </TabsTrigger>
        <TabsTrigger
          value='grid'
          className={cn(
            "flex items-center gap-2",
            viewType === "grid"
              ? "bg-primary text-primary-foreground"
              : "hover:bg-background/80",
          )}
        >
          <Grid className='h-4 w-4' />
          <span>Grid</span>
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
};

export const ViewToggleAdapter: React.FC<ViewToggleProps> = ({
  viewType,
  setViewType,
}) => {
  return <ViewToggle viewType={viewType} setViewType={setViewType} />;
};

export default ViewToggle;
